import axios from 'axios';
import { addBearerTokenInReq } from './interceptors/addBearerTokenInReq';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { checkIfSessionExpired } from './interceptors/checkIfSessionExpired';
import { AuthService } from '.';
import { getLocalAuthStorage } from 'utils/localStorage';
import { localStorageKeys } from 'constants/localStorage';
import { settIsRefreshing } from 'utils/refresh';
import { handleAuthError } from './interceptors/handleAuthError';
import { createLoginRetryInterceptor } from './interceptors/loginRetryOn5xx';
import { Mutex } from 'async-mutex';
import { URLS } from 'constants/urls';
import { addSentryLoggingInterceptor } from './interceptors/addSentryLogginInterceptor';

const mutex = new Mutex();
const saveTokensToCookies = (token, refreshToken) => {
  document.cookie = `authToken=${token}; path=/; domain=${URLS.cookieDomain}`;
  document.cookie = `refreshToken=${refreshToken}; path=/; domain=${URLS.cookieDomain}`;
};
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}
function EnsureResponseSuccess(response) {
  if (response?.status !== 200 || !response?.data?.authToken?.token) {
    const serializedResponse = {
      status: response?.status,
      statusText: response?.statusText,
      data: response?.data,
      headers: response?.headers,
      config: {
        url: response?.config?.url,
        method: response?.config?.method,
      },
    };

    throw {
      message: 'Resposta inválida da API',
      response: JSON.stringify(serializedResponse, null, 2),
    };
  }
  return response;
}

export const refreshAuthLogic = async failedRequest => {
  return mutex.runExclusive(async () => {
    const storage = getLocalAuthStorage();
    const now = Date.now();
    const expires = new Date(storage.expiresIn).getTime();

    if (now < expires) {
      failedRequest.config.headers['Authorization'] = 'Bearer ' + storage.token;

      return Promise.resolve();
    }
    const token = getCookie(`authToken`);
    const refreshToken = getCookie(`refreshToken`);

    if (failedRequest.config.url === '/v1/auth') {
      return Promise.reject(failedRequest.response);
    }

    if (failedRequest.response.status === 401) {
      if (!token || !refreshToken) {
        window.location.replace(`${process.env.REACT_APP_URL}/logout`);
        return Promise.reject(failedRequest?.response);
      }
      if (!storage.isRefreshing) {
        storage.isRefreshing = true;
        settIsRefreshing(true);

        try {
          const response = EnsureResponseSuccess(await AuthService.refreshToken({ token, refreshToken }));

          const newToken = response?.data.authToken.token;
          storage.token = newToken;
          storage.refreshToken = response?.data.authToken.refreshToken;
          storage.expiresIn = response?.data.authToken.expiresIn;
          localStorage.setItem(localStorageKeys.authContext, JSON.stringify(storage));
          saveTokensToCookies(response?.data.authToken.token, response?.data.authToken.refreshToken);
          failedRequest.config.headers['Authorization'] = 'Bearer ' + newToken;
          return Promise.resolve();
        } catch (error) {
          storage.isRefreshing = false;
          localStorage.setItem(localStorageKeys.authContext, JSON.stringify(storage));

          window.location.replace(`${process.env.REACT_APP_URL}/logout`);

          return Promise.reject(error);
        } finally {
          settIsRefreshing(false);
          storage.isRefreshing = false;
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return refreshAuthLogic(failedRequest);
      }
    }

    return Promise.reject(failedRequest);
  });
};

const AxiosMain = axios.create({
  baseURL: process.env.REACT_APP_AUTH_API,
});
const AxiosMainLogin = axios.create({
  baseURL: process.env.REACT_APP_AUTH_API,
});

const AxiosMock = axios.create({
  baseURL: process.env.REACT_APP_MOCK_API,
});

const AxiosCharts = axios.create({
  baseURL: process.env.REACT_APP_LAS_CHART_API,
});

const AxiosGlobalFilters = axios.create({
  baseURL: process.env.REACT_APP_LIGHTHOUSE_FILTERS_API,
});

const AxiosMenuLas = axios.create({
  baseURL: process.env.REACT_APP_LAS,
});
const AxiosMediaCenter = axios.create({
  baseURL: process.env.REACT_APP_MEDIA_CENTER,
});
const AxiosMediaCenterSignature = axios.create({
  baseURL: process.env.REACT_APP_SIGNATURE_IMAGE,
});

// --- Request interceptors ---
// 1️ Adiciona token
// 2️Verifica se sessão está expirada localmente e faz refresh se necessário
AxiosMediaCenter.interceptors.request.use(addBearerTokenInReq);
AxiosMediaCenter.interceptors.request.use(checkIfSessionExpired);

AxiosMediaCenterSignature.interceptors.request.use(addBearerTokenInReq);
AxiosMediaCenterSignature.interceptors.request.use(checkIfSessionExpired);

AxiosGlobalFilters.interceptors.request.use(addBearerTokenInReq);
AxiosGlobalFilters.interceptors.request.use(checkIfSessionExpired);

AxiosCharts.interceptors.request.use(addBearerTokenInReq);
AxiosCharts.interceptors.request.use(checkIfSessionExpired);

AxiosMenuLas.interceptors.request.use(addBearerTokenInReq);
AxiosMenuLas.interceptors.request.use(checkIfSessionExpired);

AxiosMain.interceptors.request.use(addBearerTokenInReq);
AxiosMain.interceptors.request.use(checkIfSessionExpired);

AxiosMainLogin.interceptors.request.use(addBearerTokenInReq);
AxiosMainLogin.interceptors.request.use(checkIfSessionExpired);

// --- Response interceptors ---
// Retry automático para erros 5xx
AxiosMain.interceptors.response.use(res => res, createLoginRetryInterceptor(AxiosMain, handleAuthError));
AxiosMainLogin.interceptors.response.use(res => res, createLoginRetryInterceptor(AxiosMainLogin, handleAuthError));

//  Refresh reativo em caso de 401 retornado do backend
createAuthRefreshInterceptor(AxiosGlobalFilters, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosMediaCenterSignature, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosMediaCenter, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosMenuLas, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosCharts, refreshAuthLogic);

// 5️Logging no Sentry
addSentryLoggingInterceptor(AxiosMain);
addSentryLoggingInterceptor(AxiosMainLogin);
addSentryLoggingInterceptor(AxiosCharts);
addSentryLoggingInterceptor(AxiosGlobalFilters);
addSentryLoggingInterceptor(AxiosMenuLas);
addSentryLoggingInterceptor(AxiosMediaCenter);
addSentryLoggingInterceptor(AxiosMediaCenterSignature);
addSentryLoggingInterceptor(AxiosMock);
export {
  AxiosMain,
  AxiosMainLogin,
  AxiosCharts,
  AxiosGlobalFilters,
  AxiosMock,
  AxiosMenuLas,
  AxiosMediaCenter,
  AxiosMediaCenterSignature,
};
